import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, CornerDownRight, Loader2, Plus, Search, Star, Table2, X } from 'lucide-react';
import { api, DEMO } from '../lib/api';

// Demo mode reads a small bundled sample of the official FY2026 release; the full
// ~98k-code library is only ever queried through the API. Lazy-imported so the
// production bundle never downloads it.
let demoCache = null;
async function demoLibrary() {
  if (!demoCache) demoCache = (await import('../data/icd10-demo.json')).default;
  return demoCache;
}
// Same clinical-abbreviation expansion the API applies (mirrors ICD10_SYNONYMS in backend/php/src/Icd10.php).
const DEMO_SYNONYMS = { ptsd: 'post-traumatic stress', mdd: 'major depressive disorder', gad: 'generalized anxiety disorder', ocd: 'obsessive-compulsive', adhd: 'attention-deficit hyperactivity', add: 'attention-deficit hyperactivity', uti: 'urinary tract infection', copd: 'chronic obstructive pulmonary', 'heart attack': 'myocardial infarction', stroke: 'cerebral infarction' };
function demoSearch(rows, term) {
  const compact = term.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
  const words = term.trim().toLowerCase();
  const alt = DEMO_SYNONYMS[words] || words;
  const isCodeLike = /^[A-Z0-9]{2,7}$/.test(compact);
  return rows
    .filter(r => (isCodeLike && r.code.replace('.', '').toUpperCase().startsWith(compact)) || r.description.toLowerCase().includes(words) || r.description.toLowerCase().includes(alt))
    .sort((a, b) => {
      const compactOf = r => r.code.replace('.', '').toUpperCase();
      const rank = r => (isCodeLike && compactOf(r) === compact ? 0 : isCodeLike && compactOf(r).startsWith(compact) ? 1 : 2);
      return (rank(a) - rank(b)) || ((b.is_billable ? 1 : 0) - (a.is_billable ? 1 : 0)) || compactOf(a).localeCompare(compactOf(b));
    })
    .slice(0, 50);
}
function demoChildren(rows, chapters, parent) {
  if (!parent) return { type: 'chapters', chapters };
  if (/^\d{2}$/.test(parent)) return { type: 'categories', parent: { chapter_code: parent, chapter_name: (chapters.find(c => c.chapter_code === parent) || {}).chapter_name }, children: rows.filter(r => r.chapter_code === parent && r.code_level === 'category') };
  const node = rows.find(r => r.code === parent);
  if (!node) return { type: 'codes', parent: null, children: [], breadcrumb: [] };
  const children = rows.filter(r => r.parent_code === parent);
  const crumb = [];
  let walk = node;
  while (walk && walk.parent_code && crumb.length < 8) { walk = rows.find(r => r.code === walk.parent_code); if (walk) crumb.unshift(walk); }
  crumb.push(node);
  return { type: 'codes', parent: { ...node, has_children: children.length > 0 }, children, breadcrumb: [{ chapter_code: node.chapter_code, chapter_name: node.chapter_name, code_level: 'chapter' }, ...crumb] };
}

function CodeRow({ item, picked, disabled, onAdd, onDrill }) {
  return (
    <div className="dx-row">
      {item.has_children
        ? <button type="button" className="dx-row-main" onClick={() => onDrill(item)} aria-label={`Browse more specific codes under ${item.code}`}><span className="dx-code">{item.code}</span><span className="dx-desc">{item.description}</span><ChevronRight size={14} className="dx-caret" /></button>
        : <div className="dx-row-main static"><span className="dx-code">{item.code}</span><span className="dx-desc">{item.description}</span></div>}
      <span className={`badge ${item.is_billable ? 'green' : 'neutral'}`}>{item.is_billable ? 'Billable' : 'Header'}</span>
      {item.is_billable && !disabled && (picked
        ? <span className="badge neutral dx-added">Added</span>
        : <button type="button" className="icon-button dx-add" aria-label={`Add ${item.code}`} onClick={() => onAdd(item)}><Plus size={15} /></button>)}
    </div>
  );
}

export default function Icd10Picker({ value, onChange, disabled }) {
  const [mode, setMode] = useState('search');
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [tree, setTree] = useState(null);
  const [browsing, setBrowsing] = useState(false);
  const [year, setYear] = useState(null);
  const [libraryError, setLibraryError] = useState('');
  const revision = useRef(0);
  const selectedCodes = useMemo(() => new Set(value.map(d => d.code.toUpperCase())), [value]);

  // Search: debounced, abortable, case-insensitive on both code prefix and description words.
  useEffect(() => {
    if (mode !== 'search') return undefined;
    const term = query.trim();
    if (term.length < 2) { setResults(null); setLibraryError(''); return undefined; }
    const current = ++revision.current;
    const controller = new AbortController();
    setSearching(true); setLibraryError('');
    const timer = setTimeout(async () => {
      try {
        if (DEMO) {
          const lib = await demoLibrary();
          if (controller.signal.aborted) return;
          setYear(`FY${lib.year} sample (demo)`); setResults(demoSearch(lib.rows, term));
        } else {
          const data = await api('/icd10/search?q=' + encodeURIComponent(term), { signal: controller.signal });
          if (controller.signal.aborted) return;
          setYear(`FY${data.year}`); setResults(data.results);
        }
      } catch (e) {
        if (e.name !== 'AbortError' && current === revision.current) { setResults([]); setLibraryError(e.message); }
      } finally { if (current === revision.current) setSearching(false); }
    }, 320);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [query, mode]);

  // Browse: open one hierarchy level (no parent = chapter list).
  const openLevel = useCallback(async (stack) => {
    setBrowsing(true); setLibraryError('');
    const parent = stack.length ? stack[stack.length - 1] : undefined;
    try {
      if (DEMO) {
        const lib = await demoLibrary();
        setYear(`FY${lib.year} sample (demo)`);
        setTree(demoChildren(lib.rows, lib.chapters, parent));
      } else {
        const data = await api('/icd10/tree' + (parent ? '?parent=' + encodeURIComponent(parent) : ''));
        setYear(`FY${data.year}`); setTree(data);
      }
    } catch (e) { setTree(null); setLibraryError(e.message); }
    finally { setBrowsing(false); }
  }, []);

  useEffect(() => { if (mode === 'browse' && tree === null && !libraryError) openLevel([]); }, [mode, tree, libraryError, openLevel]);

  // Drilling from a search result into browse mode. Categories sit one level under their
  // chapter; deeper headers sit under their own parent (skipped intermediates resolved server-side).
  const drill = useCallback((item) => {
    if (!item.has_children) return;
    setMode('browse');
    setTree(null);
    if (item.code_level === 'category') openLevel([item.chapter_code, item.code]);
    else openLevel([item.chapter_code, item.code]);
  }, [openLevel]);

  const browseCrumb = (index, crumb) => {
    if (crumb.code_level === 'chapter') { openLevel([crumb.chapter_code]); return; }
    openLevel([crumb.chapter_code || crumb.code.slice(0, 1), crumb.code]);
  };

  function add(row) {
    if (disabled) return;
    onChange([...value, { code: row.code, description: row.description, effective_year: row.effective_year ?? null, is_primary: value.length === 0 }]);
  }
  function remove(code) {
    onChange(value.filter(d => d.code !== code).map((d, i) => ({ ...d, is_primary: i === 0 })));
  }
  function makePrimary(code) {
    const target = value.find(d => d.code === code);
    if (!target) return;
    onChange([{ ...target, is_primary: true }, ...value.filter(d => d.code !== code).map(d => ({ ...d, is_primary: false }))]);
  }

  const picked = item => selectedCodes.has(item.code.toUpperCase());

  return (
    <section className={`dx-section ${disabled ? 'dx-readonly' : ''}`} aria-label="Diagnosis codes">
      <div className="dx-head">
        <h4><Table2 size={15} /> <span>Diagnoses · ICD-10-CM</span> {year && <span className="dx-year">{year}{DEMO ? '' : ' · current release'}</span>}</h4>
        {!disabled && (
          <div className="dx-tabs" role="tablist" aria-label="Code library mode">
            <button type="button" role="tab" aria-selected={mode === 'search'} className={mode === 'search' ? 'selected' : ''} onClick={() => setMode('search')}><Search size={13} /> <span>Search</span></button>
            <button type="button" role="tab" aria-selected={mode === 'browse'} className={mode === 'browse' ? 'selected' : ''} onClick={() => setMode('browse')}><CornerDownRight size={13} /> <span>Browse</span></button>
          </div>
        )}
      </div>

      {value.length > 0 && (
        <div className="dx-chips" aria-label="Selected diagnoses">
          {value.map(d => (
            <span className={`dx-chip ${d.is_primary ? 'dx-chip-primary' : ''}`} key={d.code}>
              <button type="button" className="dx-star" disabled={disabled || d.is_primary} title={d.is_primary ? 'Primary diagnosis' : 'Make primary'} aria-label={d.is_primary ? `${d.code} is the primary diagnosis` : `Make ${d.code} primary`} onClick={() => makePrimary(d.code)}>
                <Star size={12} fill={d.is_primary ? 'currentColor' : 'none'} />
              </button>
              <code>{d.code}</code>
              <span className="dx-chip-desc" title={d.description}>{d.description}</span>
              {!disabled && <button type="button" className="dx-remove" aria-label={`Remove ${d.code}`} onClick={() => remove(d.code)}><X size={13} /></button>}
            </span>
          ))}
        </div>
      )}
      {value.length === 0 && disabled && <p className="empty-copy">No diagnoses attached to this note.</p>}

      {!disabled && mode === 'search' && (
        <>
          <div className="dx-search-row">
            <Search size={15} />
            <input aria-label="Search ICD-10-CM codes" value={query} onChange={e => setQuery(e.target.value)} placeholder="Code or description — e.g. I50, heart failure, diabetes" autoComplete="off" />
            {searching && <Loader2 size={15} className="spin" />}
          </div>
          {libraryError && <p className="inline-alert danger" role="alert">{libraryError}</p>}
          {results && (
            <div className="dx-list" role="listbox" aria-label="Matching codes">
              {results.length
                ? results.map(item => <CodeRow key={item.code} item={item} picked={picked(item)} disabled={disabled} onAdd={add} onDrill={drill} />)
                : <p className="empty-copy">No codes match “{query.trim()}”. Try a shorter code prefix or another term.</p>}
            </div>
          )}
          {results === null && <p className="dx-hint">Type at least 2 characters. Partial codes (“I50”) and diagnosis words (“heart failure”, “diabetes”) both work. Add one or more diagnoses; the first is primary.</p>}
        </>
      )}

      {!disabled && mode === 'browse' && (
        <>
          {tree?.type !== 'chapters' && (
            <nav className="dx-crumbs" aria-label="Hierarchy">
              <button type="button" onClick={() => openLevel([])}>All chapters</button>
              {tree?.type === 'categories' && tree.parent && <span> / Chapter {tree.parent.chapter_code} — {tree.parent.chapter_name}</span>}
              {tree?.breadcrumb && tree.breadcrumb.map((c, i) => (
                <span key={i}> / {i === 0
                  ? <button type="button" onClick={() => browseCrumb(i, c)}>Ch. {c.chapter_code}</button>
                  : (i === tree.breadcrumb.length - 1 ? <b>{c.code}</b> : <button type="button" onClick={() => browseCrumb(i, c)}>{c.code}</button>)}</span>
              ))}
            </nav>
          )}
          {browsing && <p className="dx-hint"><Loader2 size={14} className="spin" /> Loading…</p>}
          {libraryError && <p className="inline-alert danger" role="alert">{libraryError}</p>}
          {tree && !browsing && tree.type === 'chapters' && (
            <div className="dx-chapters">
              {tree.chapters.map(c => (
                <button type="button" key={c.chapter_code} className="dx-chapter" onClick={() => openLevel([c.chapter_code])}>
                  <b>Chapter {c.chapter_code}</b>
                  <span>{c.chapter_name}</span>
                  <small>{c.categories ?? '—'} categories · {c.billable_codes ?? '—'} codes in library</small>
                </button>
              ))}
            </div>
          )}
          {tree && !browsing && tree.type !== 'chapters' && (
            <div className="dx-list">
              {tree.children.length
                ? tree.children.map(item => <CodeRow key={item.code} item={item} picked={picked(item)} disabled={disabled} onAdd={add} onDrill={i => i.has_children && openLevel([i.chapter_code, i.code])} />)
                : <p className="empty-copy">No codes below this level.</p>}
            </div>
          )}
        </>
      )}
    </section>
  );
}
